import { UiButtonDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  signal,
  inject,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { EzFormComponent } from '@template-ui/components/ez-form/ez-form.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { InputSelectOption } from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';

import {
  WorkflowNotificationRecipientTypeCode,
  WorkflowNotificationRule,
  WorkflowNotificationSeverityCode,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowEditorPendingScope } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

interface NotificationFormValues {
  recipientTypeCode: string;
  recipientTargetKey: string;
  severityCode: string;
}

const RECIPIENT_OPTIONS: InputSelectOption[] = [
  { value: 'current_assignee', viewValue: 'Person currently assigned' },
  { value: 'current_queue', viewValue: 'Everyone in the current queue' },
  { value: 'queue', viewValue: 'A specific queue' },
  { value: 'role', viewValue: 'A role or group' },
  { value: 'manager', viewValue: 'A configured manager relationship' },
  {
    value: 'external_destination',
    viewValue: 'An approved outside destination',
  },
];

const SEVERITY_OPTIONS: InputSelectOption[] = [
  { value: 'info', viewValue: 'Standard notification' },
  { value: 'warning', viewValue: 'Needs attention' },
  { value: 'critical', viewValue: 'Urgent alert' },
];

@Component({
  selector: 'phms-workflow-notification-rule-editor',
  imports: [UiButtonDirective, CommonModule, UiLibraryModule],
  templateUrl: './workflow-notification-rule-editor.component.html',
  styleUrl: './workflow-notification-rule-editor.component.scss',
})
export class WorkflowNotificationRuleEditorComponent implements OnChanges {
  @Input() rules: Array<WorkflowNotificationRule | null> | null = null;
  @Input() canAuthor = false;
  @Input() inspectionMode = false;
  @Input() embeddedContext = false;
  @Input() interactionBlocked = false;
  @Input() referenceLoading = false;
  @Input() roleCodes: string[] = [];
  @Input() queueCodes: string[] = [];
  @Input() managerTargetCodes: string[] = [];
  @Input() externalTargetCodes: string[] = [];
  @Input() triggerLabel = 'when this workflow event occurs';
  @Input() instanceId = 'workflow-notification-rule';
  @Input() ownerKey = '';
  @Input() resetVersion = 0;

  @Output() readonly rulesChange =
    new EventEmitter<Array<WorkflowNotificationRule | null> | null>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();

  @ViewChild('notificationForm')
  private notificationForm?: EzFormComponent;

  @ViewChild('notificationEditorHeading')
  private notificationEditorHeading?: ElementRef<HTMLElement>;

  @ViewChild('notificationFormHeading')
  private notificationFormHeading?: ElementRef<HTMLElement>;

  @ViewChild('notificationAddButton')
  private notificationAddButton?: ElementRef<HTMLButtonElement>;

  @ViewChildren('notificationRuleButton', { read: ElementRef })
  private notificationRuleButtons?: QueryList<ElementRef<HTMLButtonElement>>;

  readonly selectedIndex = signal<number | null>(null);
  readonly isAdding = signal(false);
  readonly formDirty = signal(false);
  readonly localError = signal<string | null>(null);
  readonly formRenderVersion = signal(0);

  formValues: NotificationFormValues = this.emptyFormValues();
  formConfig: InputConfig[] = [];

  private initiallyNullRules = true;
  private lastResetVersion: number | null = null;
  private loadedRule: WorkflowNotificationRule | null = null;
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });

  ngOnChanges(changes: SimpleChanges): void {
    if (this.lastResetVersion !== this.resetVersion || changes['ownerKey']) {
      this.lastResetVersion = this.resetVersion;
      this.initiallyNullRules = this.rules === null;
      this.resetEditor();
      return;
    }

    if (changes['rules'] && !this.formDirty()) {
      const index = this.selectedIndex();
      if (index !== null && this.rules?.[index]) {
        this.loadRule(index);
      } else if (index !== null) {
        this.resetEditor();
      }
    }

    if (
      changes['roleCodes'] ||
      changes['queueCodes'] ||
      changes['managerTargetCodes'] ||
      changes['externalTargetCodes'] ||
      changes['canAuthor'] ||
      changes['inspectionMode'] ||
      changes['interactionBlocked']
    ) {
      this.rebuildFormConfig();
    }
  }

  get ruleEntries(): Array<{
    index: number;
    rule: WorkflowNotificationRule | null;
  }> {
    return (this.rules ?? []).map((rule, index) => ({ index, rule }));
  }

  get selectedRule(): WorkflowNotificationRule | null {
    const index = this.selectedIndex();
    return index === null ? null : (this.rules?.[index] ?? null);
  }

  get editorOpen(): boolean {
    return this.isAdding() || this.selectedIndex() !== null;
  }

  get targetCatalogUnavailable(): boolean {
    const type = this.formValues.recipientTypeCode;
    return (
      this.requiresTarget(type) && this.approvedTargetCodes(type).length === 0
    );
  }

  get unavailableRecipientTypes(): Array<{ label: string; reason: string }> {
    return RECIPIENT_OPTIONS.filter(
      (option) =>
        this.requiresTarget(String(option.value)) &&
        this.approvedTargetCodes(String(option.value)).length === 0,
    ).map((option) => ({
      label: option.viewValue,
      reason: this.unavailableTargetReason(String(option.value)),
    }));
  }

  get recipientSummary(): string {
    return this.recipientLabel({
      id: '',
      recipientTypeCode: this.formValues.recipientTypeCode,
      recipientTargetKey: this.formValues.recipientTargetKey,
      severityCode: this.formValues.severityCode,
    });
  }

  get urgencySummary(): string {
    return this.severityLabel({
      id: '',
      recipientTypeCode: this.formValues.recipientTypeCode,
      recipientTargetKey: this.formValues.recipientTargetKey,
      severityCode: this.formValues.severityCode,
    });
  }

  get selectedTargetUnavailableReason(): string {
    return this.unavailableTargetReason(this.formValues.recipientTypeCode);
  }

  beginAdd(): void {
    if (!this.canEdit || this.formDirty()) {
      return;
    }
    this.isAdding.set(true);
    this.loadedRule = null;
    this.selectedIndex.set(null);
    this.formValues = this.emptyFormValues();
    this.formConfig = this.buildFormConfig();
    this.recreateForm();
    this.localError.set(null);
    this.setFormDirty(true);
    this.focusAfterRender(() => this.notificationFormHeading);
  }

  selectRule(index: number): void {
    if (this.interactionBlocked) return;
    const wasPending = this.formDirty();
    const targetId = this.rules?.[index]?.id;
    const targetNull = this.rules?.[index] === null;
    if (!this.applyPendingChanges()) {
      this.focusPendingChanges();
      return;
    }
    if (wasPending && targetId) {
      const matches = this.ruleEntries.filter(
        (entry) => entry.rule?.id === targetId,
      );
      if (matches.length !== 1) return;
      index = matches[0].index;
    } else if (wasPending && !targetNull) return;
    if (this.rules?.[index] === null) {
      this.isAdding.set(false);
      this.selectedIndex.set(index);
      this.formValues = this.emptyFormValues();
      this.formConfig = [];
      this.localError.set(null);
      return;
    }
    this.loadRule(index);
  }

  onFormChange(values: unknown): void {
    if (!this.canEdit) {
      return;
    }
    const incoming = values as Partial<NotificationFormValues>;
    const recipientTypeCode =
      incoming.recipientTypeCode ?? this.formValues.recipientTypeCode;
    const recipientChanged =
      recipientTypeCode !== this.formValues.recipientTypeCode;
    this.formValues = {
      recipientTypeCode,
      recipientTargetKey: recipientChanged
        ? ''
        : (incoming.recipientTargetKey ?? this.formValues.recipientTargetKey),
      severityCode: incoming.severityCode ?? this.formValues.severityCode,
    };
    if (recipientChanged) {
      this.formConfig = this.buildFormConfig(this.selectedRule ?? undefined);
      this.recreateForm();
    }
    this.setFormDirty(true);
    this.localError.set(null);
  }

  pendingEditorScopes(latestRules: () => Array<WorkflowNotificationRule | null> | null = () => this.rules): readonly WorkflowEditorPendingScope[] {
    const matches = () => (latestRules() ?? []).map((rule, index) => ({ rule, index })).filter(row => !!this.loadedRule?.id && row.rule?.id === this.loadedRule.id);
    return [{
      id: 'notification-recipients', label: 'notification recipient input',
      state: () => {
        if (!this.formDirty()) return 'clean';
        if (!this.isAdding() && (matches().length !== 1 || JSON.stringify(matches()[0].rule) !== JSON.stringify(this.loadedRule))) return 'stale';
        if (!this.canEdit || !this.notificationForm?.theFormGroup?.valid) return 'invalid';
        const type = this.formValues.recipientTypeCode.trim(), target = this.formValues.recipientTargetKey.trim(), severity = this.formValues.severityCode.trim();
        if (!RECIPIENT_OPTIONS.some(option => option.value === type) || !SEVERITY_OPTIONS.some(option => option.value === severity) ||
          (this.requiresTarget(type) && !target) || this.targetCatalogUnavailable ||
          (this.requiresTarget(type) && !this.approvedTargetCodes(type).includes(target))) return 'invalid';
        return 'valid';
      },
      fingerprint: () => JSON.stringify([this.formDirty(), this.isAdding(), this.loadedRule, this.formValues, latestRules(), this.ownerKey, this.resetVersion,
        this.roleCodes, this.queueCodes, this.managerTargetCodes, this.externalTargetCodes,
        this.canAuthor, this.inspectionMode, this.interactionBlocked, this.referenceLoading]),
      apply: () => this.applyPendingChanges(latestRules()),
      discard: () => {
        const latest = latestRules(), selected = matches();
        this.rules = latest;
        if (!this.isAdding()) this.selectedIndex.set(selected.length === 1 ? selected[0].index : null);
        this.discardPendingChanges();
      },
      focus: () => this.focusPendingChanges(),
    }];
  }
  applyPendingChanges(
    latestRules?: Array<WorkflowNotificationRule | null> | null,
  ): boolean {
    if (!this.formDirty()) return true;
    if (!this.canEdit) return false;
    if (latestRules !== undefined) {
      const selected = this.selectedIndex();
        const id = this.loadedRule?.id ?? (selected === null ? null : this.rules?.[selected]?.id);
      if (!this.isAdding()) {
        const matches = (latestRules ?? [])
          .map((rule, index) => ({ rule, index }))
          .filter(({ rule }) => !!id && rule?.id === id);
        if (matches.length !== 1 || JSON.stringify(matches[0].rule) !== JSON.stringify(this.loadedRule)) {
          this.showError(
            'The selected recipient is no longer available. Your edits remain open.',
          );
          return false;
        }
        this.selectedIndex.set(matches[0].index);
      }
      this.rules = latestRules;
    }
    const capture = this.rulesChange.subscribe((value) => (this.rules = value));
    try {
      this.applyRule();
      return !this.formDirty();
    } finally {
      capture.unsubscribe();
    }
  }

  applyRule(): void {
    if (!this.canEdit || !this.ensureFormValid()) {
      return;
    }
    const recipientTypeCode =
      this.formValues.recipientTypeCode.trim() as WorkflowNotificationRecipientTypeCode;
    const recipientTargetKey = this.requiresTarget(recipientTypeCode)
      ? this.formValues.recipientTargetKey.trim()
      : null;
    const severityCode =
      this.formValues.severityCode.trim() as WorkflowNotificationSeverityCode;
    if (
      !RECIPIENT_OPTIONS.some((option) => option.value === recipientTypeCode) ||
      !SEVERITY_OPTIONS.some((option) => option.value === severityCode)
    ) {
      this.showError(
        'Choose a supported recipient type and urgency before applying changes.',
      );
      return;
    }
    if (this.requiresTarget(recipientTypeCode) && !recipientTargetKey) {
      this.showError('Choose an approved notification target.');
      return;
    }
    if (this.targetCatalogUnavailable) {
      this.showError(this.selectedTargetUnavailableReason);
      return;
    }
    if (
      recipientTargetKey &&
      !this.approvedTargetCodes(recipientTypeCode).includes(recipientTargetKey)
    ) {
      this.showError('Choose an active, approved notification target.');
      return;
    }

    const index = this.selectedIndex();
    const current = index === null ? null : (this.rules?.[index] ?? null);
    const nextRule: WorkflowNotificationRule = {
      ...current,
      id: current?.id ?? this.createRuleId(),
      recipientTypeCode,
      recipientTargetKey,
      severityCode,
    };
    const nextRules = [...(this.rules ?? [])];
    if (index === null) {
      nextRules.push(nextRule);
      this.selectedIndex.set(nextRules.length - 1);
    } else {
      nextRules[index] = nextRule;
    }
    this.isAdding.set(false);
    this.formValues = this.toFormValues(nextRule);
    this.loadedRule = structuredClone(nextRule);
    this.formConfig = this.buildFormConfig(nextRule);
    this.setFormDirty(false);
    this.localError.set(null);
    this.rulesChange.emit(nextRules);
  }

  cancelEdit(): void {
    const wasAdding = this.isAdding();
    this.restoreAppliedEditorState();
    if (wasAdding) {
      this.focusAfterRender(
        () => this.notificationAddButton ?? this.notificationEditorHeading,
      );
    }
  }

  finishEdit(): void {
    if (this.interactionBlocked || this.referenceLoading || this.formDirty())
      return;
    const index = this.selectedIndex();
    this.resetEditor();
    this.focusAfterRender(
      () =>
        (index === null
          ? undefined
          : this.notificationRuleButtons?.get(index)) ??
        this.notificationAddButton ??
        this.notificationEditorHeading,
    );
  }

  discardPendingChanges(): void {
    this.restoreAppliedEditorState();
  }

  focusPendingChanges(): void {
    this.focusAfterRender(
      () => this.notificationFormHeading ?? this.notificationEditorHeading,
    );
  }

  removeSelected(): void {
    const index = this.selectedIndex();
    if (index === null || !this.canEdit) {
      return;
    }
    const nextRules = (this.rules ?? []).filter(
      (_, ruleIndex) => ruleIndex !== index,
    );
    this.resetEditor();
    this.rulesChange.emit(
      nextRules.length === 0 && this.initiallyNullRules ? null : nextRules,
    );
    this.focusAfterRender(
      () =>
        this.notificationRuleButtons?.get(
          Math.min(index, this.notificationRuleButtons.length - 1),
        ) ??
        this.notificationAddButton ??
        this.notificationEditorHeading,
    );
  }

  recipientLabel(rule: WorkflowNotificationRule): string {
    const target = rule.recipientTargetKey
      ? `: ${this.humanizeCode(rule.recipientTargetKey)}`
      : '';
    switch (rule.recipientTypeCode) {
      case 'current_assignee':
        return 'Person currently assigned';
      case 'current_queue':
        return 'Everyone in the current queue';
      case 'queue':
        return `Queue${target}`;
      case 'role':
        return `Role or group${target}`;
      case 'manager':
        return `Manager relationship${target}`;
      case 'external_destination':
        return `Outside destination${target}`;
      default:
        return this.humanizeCode(rule.recipientTypeCode) + target;
    }
  }

  severityLabel(rule: WorkflowNotificationRule): string {
    return (
      SEVERITY_OPTIONS.find((option) => option.value === rule.severityCode)
        ?.viewValue ?? this.humanizeCode(rule.severityCode)
    );
  }

  trackRule(_index: number, entry: { index: number }): number {
    return entry.index;
  }

  trackFormRenderVersion(_index: number, renderVersion: number): number {
    return renderVersion;
  }

  get formRenderVersions(): number[] {
    return [this.formRenderVersion()];
  }

  private get canEdit(): boolean {
    return (
      this.canAuthor &&
      !this.inspectionMode &&
      !this.interactionBlocked &&
      !this.referenceLoading
    );
  }

  private loadRule(index: number): void {
    const rule = this.rules?.[index];
    if (!rule) {
      return;
    }
    this.isAdding.set(false);
    this.loadedRule = structuredClone(rule);
    this.selectedIndex.set(index);
    this.formValues = this.toFormValues(rule);
    this.formConfig = this.buildFormConfig(rule);
    this.recreateForm();
    this.setFormDirty(false);
    this.localError.set(null);
  }

  private resetEditor(): void {
    this.loadedRule = null;
    this.isAdding.set(false);
    this.selectedIndex.set(null);
    this.formValues = this.emptyFormValues();
    this.formConfig = [];
    this.setFormDirty(false);
    this.localError.set(null);
  }

  private restoreAppliedEditorState(): void {
    if (this.isAdding()) {
      this.resetEditor();
      return;
    }
    const index = this.selectedIndex();
    if (index !== null && this.rules?.[index]) {
      this.loadRule(index);
    } else {
      this.resetEditor();
    }
  }

  private rebuildFormConfig(): void {
    if (this.editorOpen && (this.isAdding() || this.selectedRule)) {
      this.formConfig = this.buildFormConfig(this.selectedRule ?? undefined);
      this.recreateForm();
    }
  }

  private recreateForm(): void {
    this.formRenderVersion.update((version) => version + 1);
  }

  private buildFormConfig(
    existingRule?: WorkflowNotificationRule,
  ): InputConfig[] {
    const recipientTypeCode = this.formValues.recipientTypeCode;
    const targetOptions = this.targetOptions(recipientTypeCode, existingRule);
    const targetRequired = this.requiresTarget(recipientTypeCode);
    return [
      {
        name: 'recipientTypeCode',
        type: EzFormInputType.SELECT,
        label: 'Who to notify',
        row: 1,
        width: 'fill',
        options: this.withPreservedOption(
          RECIPIENT_OPTIONS.filter(
            (option) =>
              !this.requiresTarget(String(option.value)) ||
              this.approvedTargetCodes(String(option.value)).length > 0 ||
              option.value === recipientTypeCode,
          ),
          existingRule?.recipientTypeCode,
        ),
        validation: [Validators.required],
      },
      ...(targetRequired
        ? [
            {
              name: 'recipientTargetKey',
              type: EzFormInputType.SELECT,
              label: this.targetLabel(recipientTypeCode),
              row: 2,
              width: 'fill',
              options: targetOptions,
              validation: [Validators.required],
            } as InputConfig,
          ]
        : []),
      {
        name: 'severityCode',
        type: EzFormInputType.SELECT,
        label: 'Urgency',
        row: 3,
        width: 'fill',
        options: this.withPreservedOption(
          SEVERITY_OPTIONS,
          existingRule?.severityCode,
        ),
        validation: [Validators.required],
      },
    ];
  }

  private withPreservedOption(
    options: InputSelectOption[],
    savedValue?: string,
  ): InputSelectOption[] {
    return savedValue && !options.some((option) => option.value === savedValue)
      ? [
          ...options,
          {
            value: savedValue,
            viewValue: `${this.humanizeCode(savedValue)} (unsupported saved value)`,
          },
        ]
      : options;
  }

  private unavailableTargetReason(recipientTypeCode: string): string {
    switch (recipientTypeCode) {
      case 'queue':
        return 'No active, approved queues are available for this workflow.';
      case 'role':
        return 'No active, approved roles or groups are available for this workflow.';
      case 'manager':
        return 'No approved manager relationships are configured for this deployment.';
      case 'external_destination':
        return 'No approved outside destinations are configured for this deployment.';
      default:
        return '';
    }
  }

  private targetOptions(
    recipientTypeCode: string,
    existingRule?: WorkflowNotificationRule,
  ): InputSelectOption[] {
    if (!this.requiresTarget(recipientTypeCode)) {
      return [{ value: '', viewValue: 'Uses the active work item' }];
    }
    const activeCodes = this.approvedTargetCodes(recipientTypeCode);
    const codes = [...activeCodes];
    const existingCode =
      existingRule?.recipientTypeCode === recipientTypeCode
        ? existingRule.recipientTargetKey
        : null;
    if (existingCode && !codes.includes(existingCode)) {
      codes.push(existingCode);
    }
    return codes.map((code) => ({
      value: code,
      viewValue: activeCodes.includes(code)
        ? this.humanizeCode(code)
        : `${this.humanizeCode(code)} (not approved)`,
    }));
  }

  private approvedTargetCodes(recipientTypeCode: string): string[] {
    switch (recipientTypeCode) {
      case 'queue':
        return this.queueCodes;
      case 'role':
        return this.roleCodes;
      case 'manager':
        return this.managerTargetCodes;
      case 'external_destination':
        return this.externalTargetCodes;
      default:
        return [];
    }
  }

  private targetLabel(recipientTypeCode: string): string {
    switch (recipientTypeCode) {
      case 'queue':
        return 'Queue';
      case 'role':
        return 'Role or group';
      case 'manager':
        return 'Manager relationship';
      case 'external_destination':
        return 'Approved destination';
      default:
        return 'Target';
    }
  }

  private requiresTarget(recipientTypeCode: string): boolean {
    return ['queue', 'role', 'manager', 'external_destination'].includes(
      recipientTypeCode,
    );
  }

  private ensureFormValid(): boolean {
    const formGroup = this.notificationForm?.theFormGroup;
    if (formGroup?.valid) {
      return true;
    }
    formGroup?.markAllAsTouched();
    this.showError('Complete the notification recipient and urgency fields.');
    return false;
  }

  private showError(message: string): void {
    this.localError.set(message);
  }

  private focusAfterRender(
    target: () => ElementRef<HTMLElement> | undefined,
  ): void {
    setTimeout(() => { if (!this.ownerViews?.focusSuspended()) target()?.nativeElement.focus(); });
  }

  private setFormDirty(value: boolean): void {
    if (this.formDirty() === value) {
      return;
    }
    this.formDirty.set(value);
    this.pendingChangesChange.emit(value);
  }

  private emptyFormValues(): NotificationFormValues {
    return {
      recipientTypeCode: 'current_queue',
      recipientTargetKey: '',
      severityCode: 'info',
    };
  }

  private toFormValues(rule: WorkflowNotificationRule): NotificationFormValues {
    return {
      recipientTypeCode: rule.recipientTypeCode,
      recipientTargetKey: rule.recipientTargetKey ?? '',
      severityCode: rule.severityCode,
    };
  }

  private createRuleId(): string {
    const randomId =
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return `notification.${randomId}`;
  }

  private humanizeCode(code: string): string {
    const words = code.replace(/[._-]+/g, ' ').trim();
    return words
      ? words.charAt(0).toUpperCase() + words.slice(1)
      : 'Not specified';
  }
}
